import { Box, makeStyles } from '@material-ui/core';
import {
  EDGE_COLORS,
  EDGE_WIDTHS,
  EDGE_LABELS,
  SOLID_TIERS,
  type EdgeTier,
} from './TopologyEdge';

export const NODE_COLORS = {
  system: '#00897B',
  component: '#4da3ff',
  api: '#ffb366',
  resource: '#66bb6a',
} as const;

const useStyles = makeStyles(() => ({
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
  },
  item: { display: 'flex', alignItems: 'center', gap: 6 },
  dot: { width: 10, height: 10, borderRadius: 2 },
  label: { color: '#a0a4a8', fontSize: 11 },
}));

interface GraphLegendProps {
  /** Systems are groups in the ELK diagram but real nodes in the force view. */
  includeSystem?: boolean;
  includeExternal?: boolean;
  /** Only the builder resolves pending targets, so this tier is hidden elsewhere by default. */
  includePendingTarget?: boolean;
}

export const GraphLegend = ({ includeSystem, includeExternal, includePendingTarget }: GraphLegendProps) => {
  const classes = useStyles();

  const nodes = [
    ...(includeSystem ? [{ label: 'System', color: NODE_COLORS.system }] : []),
    { label: 'Component', color: NODE_COLORS.component },
    { label: 'API', color: NODE_COLORS.api },
    { label: 'Resource', color: NODE_COLORS.resource },
  ];

  return (
    <Box className={classes.legend}>
      {nodes.map(item => (
        <Box key={item.label} className={classes.item}>
          <Box className={classes.dot} style={{ background: item.color }} />
          <span className={classes.label}>{item.label}</span>
        </Box>
      ))}

      {includeExternal && (
        <Box className={classes.item}>
          <Box
            className={classes.dot}
            style={{ border: '1.5px dashed #6e7479', borderRadius: 2, background: 'transparent' }}
          />
          <span className={classes.label}>Defined elsewhere</span>
        </Box>
      )}

      {(Object.keys(EDGE_COLORS) as EdgeTier[])
        .filter(tier => tier !== 'pendingTarget' || includePendingTarget)
        .map(tier => (
        <Box key={tier} className={classes.item}>
          <Box
            style={{
              width: 18,
              height: 0,
              borderTop: `${EDGE_WIDTHS[tier]}px ${SOLID_TIERS.has(tier) ? 'solid' : 'dashed'} ${EDGE_COLORS[tier]}`,
            }}
          />
          <span className={classes.label}>{EDGE_LABELS[tier]}</span>
        </Box>
      ))}
    </Box>
  );
};
